import type { FC } from 'react';
import { Helmet } from 'react-helmet-async';
import ArrowLeftIcon from '@untitled-ui/icons-react/build/esm/ArrowLeft';
import ArrowRightIcon from '@untitled-ui/icons-react/build/esm/ArrowRight';
import { Box, Button, Container, Stack, SvgIcon, Typography } from '@mui/material';
import { RouterLink } from 'src/components/router-link';
import { Seo } from 'src/components/seo';
import { usePageView } from 'src/hooks/use-page-view';
import { paths } from 'src/paths';
import type { Page as PageType } from 'src/types/page';

const Page: PageType = () => {
  usePageView();

  return (
    <>
      <Seo
        title="Case Studies | Mystic Media Film"
        description="In-depth case studies from Mystic Media Film. Full project breakdowns with scope, process, and measurable results. Coming soon."
      />
      <Helmet>
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <Box
        component="main"
        sx={{
          background: '#ffffff',
          minHeight: 'calc(100vh - 96px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pt: { xs: 14, md: 18 },
          pb: { xs: 12, md: 16 }
        }}
      >
        {/* Subtle blue glow behind the content */}
        <Box
          sx={{
            position: 'absolute',
            top: '40%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: 520, md: 880 },
            height: { xs: 520, md: 880 },
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(37, 99, 235, 0.08) 0%, transparent 65%)',
            filter: 'blur(60px)',
            pointerEvents: 'none'
          }}
        />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Stack alignItems="center" spacing={{ xs: 4, md: 5 }} sx={{ textAlign: 'center' }}>
            {/* Back link */}
            <Box
              component={RouterLink}
              href={paths.projects.index}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                color: '#52525b',
                textDecoration: 'none',
                fontFamily: '"Roboto Mono", monospace',
                fontSize: 12,
                letterSpacing: 1.5,
                textTransform: 'uppercase',
                transition: 'color 0.2s ease',
                '&:hover': { color: '#0a0a0b' }
              }}
            >
              <SvgIcon sx={{ fontSize: 16 }}><ArrowLeftIcon /></SvgIcon>
              Back to projects
            </Box>

            {/* Eyebrow pill */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{
                pl: 0.75,
                pr: 2,
                py: 0.5,
                borderRadius: 999,
                background: '#ffffff',
                border: '1px solid #e4e4e7',
                boxShadow: '0 4px 14px -4px rgba(15, 23, 42, 0.06)'
              }}
            >
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: '#2563eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ffffff',
                  fontSize: 12,
                  fontWeight: 700
                }}
              >
                ✶
              </Box>
              <Typography
                sx={{
                  fontFamily: '"Roboto Mono", monospace',
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: 1.5,
                  textTransform: 'uppercase',
                  color: '#0a0a0b'
                }}
              >
                Case Studies · Coming soon
              </Typography>
            </Stack>

            {/* Headline */}
            <Typography
              component="h1"
              sx={{
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                fontSize: { xs: 44, md: 72 },
                fontWeight: 700,
                lineHeight: 1.05,
                letterSpacing: '-0.035em',
                color: '#0a0a0b'
              }}
            >
              In-depth case studies are on the way.
            </Typography>

            {/* Sub */}
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: 17, md: 19 },
                fontWeight: 400,
                lineHeight: 1.6,
                color: '#52525b',
                maxWidth: 620
              }}
            >
              Detailed breakdowns of select Mystic Media Film engagements. Project scope, creative approach, production process, and measurable results. In the meantime, browse the full portfolio or get in touch to discuss a project.
            </Typography>

            {/* CTAs */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ pt: 1 }}
            >
              <Button
                component={RouterLink}
                href={paths.projects.index}
                variant="contained"
                endIcon={
                  <SvgIcon sx={{ fontSize: 18 }}>
                    <ArrowRightIcon />
                  </SvgIcon>
                }
                sx={{
                  py: 1.5,
                  px: 4,
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  borderRadius: 2,
                  background: '#2563eb',
                  color: '#ffffff',
                  textTransform: 'none',
                  boxShadow: '0 8px 24px -8px rgba(37, 99, 235, 0.45)',
                  '&:hover': {
                    background: '#1d4ed8',
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                View all projects
              </Button>
              <Button
                component={RouterLink}
                href={paths.contact}
                variant="outlined"
                sx={{
                  py: 1.5,
                  px: 4,
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  borderRadius: 2,
                  borderColor: '#e4e4e7',
                  borderWidth: 1.5,
                  color: '#0a0a0b',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#2563eb',
                    color: '#2563eb',
                    backgroundColor: 'rgba(37, 99, 235, 0.04)',
                    borderWidth: 1.5
                  }
                }}
              >
                Start a project
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default Page;
